import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";

function Field({ label, name, value, onChange, type = "text", help, testid }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        data-testid={testid || `field-${name}`}
        className="input-line mt-3"
      />
      {help && <p className="mt-2 text-[12px] text-ink/50 italic">{help}</p>}
    </div>
  );
}

function TextArea({ label, name, value, onChange, help, testid, rows = 3 }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <textarea
        name={name}
        value={value ?? ""}
        onChange={onChange}
        rows={rows}
        data-testid={testid || `field-${name}`}
        className="input-line mt-3 resize-y"
      />
      {help && <p className="mt-2 text-[12px] text-ink/50 italic">{help}</p>}
    </div>
  );
}

export default function SiteSettings() {
  const { reload } = useSiteSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/site-settings").then(r => setForm(r.data));
  }, []);

  if (!form) return <p className="text-ink/50 text-sm">Loading…</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { _id, ...payload } = form;
      const r = await api.put("/admin/site-settings", payload);
      setForm(r.data);
      await reload();
      toast.success("Site settings saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="site-settings-page">
      <p className="eyebrow">Content</p>
      <h1 className="mt-3 font-serif text-4xl text-jade font-light">Site Settings</h1>
      <p className="mt-3 text-ink/65 text-[14px] font-light max-w-2xl">
        These values appear in the footer, contact page, page metadata and trust marks across the public site.
      </p>

      <form onSubmit={save} className="mt-12 space-y-14">
        {/* Brand */}
        <section className="space-y-6">
          <p className="eyebrow text-copper">Brand</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Brand name" name="brand_name" value={form.brand_name} onChange={set("brand_name")} />
            <Field label="Tagline" name="tagline" value={form.tagline} onChange={set("tagline")} />
          </div>
          <TextArea
            label="Brand description (footer)"
            name="footer_blurb"
            value={form.footer_blurb}
            onChange={set("footer_blurb")}
            help="Short paragraph shown beneath the wordmark in the footer."
          />
        </section>

        {/* Contact */}
        <section className="space-y-6">
          <p className="eyebrow text-copper">Contact</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Sales email" name="email" value={form.email} onChange={set("email")} type="email" />
            <Field label="Phone" name="phone" value={form.phone} onChange={set("phone")} />
            <Field label="Weekday hours" name="hours_weekday" value={form.hours_weekday} onChange={set("hours_weekday")} />
            <Field label="Weekend hours" name="hours_weekend" value={form.hours_weekend} onChange={set("hours_weekend")} />
          </div>
        </section>

        {/* Address */}
        <section className="space-y-6">
          <p className="eyebrow text-copper">Office address</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Address line 1" name="address_line1" value={form.address_line1} onChange={set("address_line1")} />
            <Field label="Address line 2" name="address_line2" value={form.address_line2} onChange={set("address_line2")} />
            <Field label="Postcode" name="postcode" value={form.postcode} onChange={set("postcode")} />
            <Field label="Country" name="country" value={form.country} onChange={set("country")} />
          </div>
        </section>

        {/* Trust marks */}
        <section className="space-y-6">
          <p className="eyebrow text-copper">Trust marks &amp; legal</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="ABTA number" name="abta_number" value={form.abta_number} onChange={set("abta_number")} help="Example: L5630" />
            <Field label="ATOL number" name="atol_number" value={form.atol_number} onChange={set("atol_number")} help="Example: 11288" />
            <Field label="Company registration" name="company_no" value={form.company_no} onChange={set("company_no")} />
            <Field label="VAT number" name="vat_no" value={form.vat_no} onChange={set("vat_no")} />
          </div>
        </section>

        {/* Social */}
        <section className="space-y-6">
          <p className="eyebrow text-copper">Social links</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Instagram URL" name="instagram_url" value={form.instagram_url} onChange={set("instagram_url")} />
            <Field label="Facebook URL" name="facebook_url" value={form.facebook_url} onChange={set("facebook_url")} />
            <Field label="LinkedIn URL" name="linkedin_url" value={form.linkedin_url} onChange={set("linkedin_url")} />
            <Field label="X / Twitter URL" name="twitter_url" value={form.twitter_url} onChange={set("twitter_url")} />
          </div>
        </section>

        {/* SEO */}
        <section className="space-y-6">
          <p className="eyebrow text-copper">Default SEO</p>
          <Field label="Default page title" name="seo_default_title" value={form.seo_default_title} onChange={set("seo_default_title")} />
          <TextArea label="Default meta description" name="seo_default_description" value={form.seo_default_description} onChange={set("seo_default_description")} />
        </section>

        <div className="pt-4 flex justify-end">
          <button
            data-testid="site-settings-save"
            disabled={saving}
            type="submit"
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
